import { useState } from 'react';
import { ChevronDown, ChevronRight, ListFilter } from 'lucide-react';
import type { RunSummary } from '../../types/customerDuplicates';

interface RejectedByRule {
    byExclusion: Record<string, number>;
    byRule: Record<string, { candidates: number; matched: number; rejected: number }>;
}

const EXCLUSION_LABELS: Record<string, string> = {
    EXCL_COUNTRY_VAT_CONFLICT: 'Different country and different VAT number',
    EXCL_NO_RULE_MATCHED: 'No rule matched',
    EXCL_NAME_TOO_SHORT: 'Normalised name too short (< 3 characters)',
    EXCL_FREEMAIL_DOMAIN: 'Shared domain is a free-mail provider',
    EXCL_EMAIL_NO_NAME_OVERLAP: 'Shared mail domain but no name overlap',
    EXCL_BLOCK_TOO_LARGE: 'Blocking key shared by too many customers',
};

/**
 * F6 of the plan: "a silent filter cannot report". `CustomerDuplicateRun.RejectedByRule` already
 * holds every candidate a rule or an exclusion turned down, with a count — this just renders it,
 * unfiltered (rejected candidates never became a pair, so the table's filters have nothing to
 * narrow here). `EXCL_COUNTRY_VAT_CONFLICT` sitting at 10'683 is not a bug: it is F3 working
 * (`ASCO Kohlensäure AG` CH vs DE) — shown as a number so nobody mistakes it for one.
 */
export function RejectedCandidates({ run }: { run: RunSummary | null }) {
    const [open, setOpen] = useState(false);
    if (!run?.rejectedByRule) return null;

    let parsed: RejectedByRule | null = null;
    try {
        parsed = JSON.parse(run.rejectedByRule) as RejectedByRule;
    } catch {
        return null;
    }
    const exclusions = Object.entries(parsed.byExclusion ?? {}).filter(([, n]) => n > 0);
    const totalExcluded = exclusions.reduce((a, [, n]) => a + n, 0);
    if (totalExcluded === 0) return null;

    return (
        <div className="rounded border border-gray-200 bg-white">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50"
            >
                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <ListFilter className="h-4 w-4 text-gray-400" />
                <span>
                    {totalExcluded.toLocaleString('de-CH')} candidates rejected before becoming a pair
                    (run #{run.runId})
                </span>
            </button>
            {open && (
                <div className="border-t border-gray-100 px-3 py-2 text-sm">
                    <ul className="space-y-1">
                        {exclusions.map(([code, n]) => (
                            <li key={code} className="flex items-baseline justify-between gap-2">
                                <span className="text-gray-600">{EXCLUSION_LABELS[code] ?? code}</span>
                                <span className="font-medium text-gray-900">{n.toLocaleString('de-CH')}</span>
                            </li>
                        ))}
                    </ul>
                    {Object.keys(parsed.byRule ?? {}).length > 0 && (
                        <>
                            <div className="mt-3 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                Per rule (blocking candidates → confirmed → rejected)
                            </div>
                            <ul className="grid grid-cols-1 gap-x-6 gap-y-0.5 sm:grid-cols-2">
                                {Object.entries(parsed.byRule).map(([rule, s]) => (
                                    <li key={rule} className="flex items-baseline justify-between gap-2 text-xs text-gray-600">
                                        <span className="font-mono">{rule}</span>
                                        <span>{s.candidates.toLocaleString('de-CH')} → {s.matched.toLocaleString('de-CH')} → {s.rejected.toLocaleString('de-CH')}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
